import React from 'react';
import { MdClose, MdCheckCircle, MdError, MdWarning, MdInfo } from 'react-icons/md';

const ResultModal = ({ 
  isOpen, 
  onClose, 
  type = 'success', // 'success', 'error', 'warning', 'info'
  operation = 'Operation', // 'Created', 'Updated', 'Deleted', etc.
  itemType = 'Item', // 'Zone', 'Officer', 'Trader', etc.
  itemName = '', // Name of the item that was operated on
  message = '',
  details = null,
  autoClose = true,
  autoCloseDelay = 4000
}) => {
  React.useEffect(() => {
    if (isOpen && autoClose && type === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose, type]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <MdCheckCircle size={64} style={{ color: '#28a745' }} />;
      case 'error':
        return <MdError size={64} style={{ color: '#dc3545' }} />;
      case 'warning':
        return <MdWarning size={64} style={{ color: '#ffc107' }} />;
      default:
        return <MdInfo size={64} style={{ color: '#17a2b8' }} />;
    }
  };

  const getTitle = () => {
    if (message) return message;
    
    switch (type) {
      case 'success':
        return `${itemType} ${operation} Successfully!`;
      case 'error':
        return `Failed to ${operation.toLowerCase()} ${itemType}`;
      case 'warning':
        return `${operation} ${itemType} Warning`;
      default:
        return `${operation} ${itemType}`;
    }
  };

  const getDefaultMessage = () => {
    switch (type) {
      case 'success':
        return `${itemName ? `"${itemName}"` : `The ${itemType.toLowerCase()}`} has been ${operation.toLowerCase()} successfully.`;
      case 'error':
        return `There was an error ${operation.toLowerCase()} ${itemName ? `"${itemName}"` : `the ${itemType.toLowerCase()}`}. Please try again.`;
      case 'warning':
        return `${operation} ${itemName ? `"${itemName}"` : `the ${itemType.toLowerCase()}`} completed with warnings.`;
      default:
        return `${operation} ${itemName ? `"${itemName}"` : `the ${itemType.toLowerCase()}`} has been processed.`;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return '#d4edda';
      case 'error':
        return '#f8d7da';
      case 'warning':
        return '#fff3cd';
      default:
        return '#d1ecf1';
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return '#c3e6cb';
      case 'error':
        return '#f5c6cb';
      case 'warning':
        return '#ffeaa7';
      default:
        return '#bee5eb';
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case 'success':
        return '#28a745';
      case 'error':
        return '#dc3545';
      case 'warning':
        return '#ffc107';
      default:
        return '#17a2b8';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease-in-out'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 32,
        width: '90%',
        maxWidth: 500,
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        textAlign: 'center',
        animation: 'slideIn 0.3s ease-out'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 8,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MdClose size={24} style={{ color: '#666' }} />
        </button>

        {/* Icon */}
        <div style={{ marginBottom: 20 }}>
          {getIcon()}
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 16px 0',
          color: '#333',
          fontSize: 22,
          fontWeight: 'bold',
        }}>
          {getTitle()}
        </h2>

        {/* Message */}
        <div style={{
          backgroundColor: getBackgroundColor(),
          border: `1px solid ${getBorderColor()}`,
          borderRadius: 8,
          padding: 20,
          marginBottom: 20,
        }}>
          <p style={{
            margin: 0,
            color: '#333',
            fontSize: 16,
            lineHeight: 1.5,
          }}>
            {getDefaultMessage()}
          </p>
        </div>

        {/* Details */}
        {details && (
          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            textAlign: 'left',
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666', fontWeight: 'bold' }}>
              Details:
            </h4>
            <div style={{ fontSize: 14, color: '#333' }}>
              {typeof details === 'string' ? (
                <p style={{ margin: 0 }}>{details}</p>
              ) : (
                details
              )}
            </div>
          </div>
        )}

        {/* Auto-close indicator */}
        {autoClose && type === 'success' && (
          <div style={{
            fontSize: 12,
            color: '#666',
            marginBottom: 20,
            fontStyle: 'italic'
          }}>
            This dialog will close automatically in {autoCloseDelay / 1000} seconds
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: 6,
            backgroundColor: getButtonColor(),
            color: 'white',
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 'bold',
            minWidth: 120,
          }}
        >
          {type === 'error' ? 'Try Again' : 'OK'}
        </button>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default ResultModal;
